import React from 'react';
import { Checkbox } from '../components/ui/checkbox';
import { Label } from '../components/ui/label';
import { Slider } from '../components/ui/slider';
import { Button } from '../components/ui/button';
import { X } from 'lucide-react';

const games = [
  { id: 'yugioh', label: 'Yu-Gi-Oh!' },
  { id: 'magic', label: 'Magic: The Gathering' },
  { id: 'pokemon', label: 'Pokémon' },
  { id: 'accessory', label: 'Accessoires' },
];

const categories = [
  { id: 'deck', label: 'Decks' },
  { id: 'booster', label: 'Boosters' },
  { id: 'single', label: 'Cartes à l\'unité' },
];

const RARITIES_BY_GAME = {
  yugioh: [
    { id: 'common', label: 'Common' },
    { id: 'rare', label: 'Rare' },
    { id: 'super_rare', label: 'Super Rare' },
    { id: 'ultra_rare', label: 'Ultra Rare' },
    { id: 'secret_rare', label: 'Secret Rare' },
    { id: 'starlight_rare', label: 'Starlight Rare' },
    { id: 'ghost_rare', label: 'Ghost Rare' },
    { id: 'qcsr', label: 'Quarter Century Secret Rare' },
  ],
  magic: [
    { id: 'common', label: 'Common' },
    { id: 'uncommon', label: 'Uncommon' },
    { id: 'rare', label: 'Rare' },
    { id: 'mythic_rare', label: 'Mythic Rare' },
    { id: 'special', label: 'Special / Bonus' },
  ],
  pokemon: [
    { id: 'common', label: 'Common' },
    { id: 'uncommon', label: 'Uncommon' },
    { id: 'rare', label: 'Rare' },
    { id: 'holo_rare', label: 'Holo Rare' },
    { id: 'ultra_rare', label: 'Ultra Rare' },
    { id: 'full_art', label: 'Full Art' },
    { id: 'secret_rare', label: 'Secret Rare' },
    { id: 'illustration_rare', label: 'Illustration Rare' },
    { id: 'special_art_rare', label: 'Special Art Rare' },
    { id: 'hyper_rare', label: 'Hyper Rare' },
  ],
};

export const FilterSidebar = ({ filters, onFilterChange, onClearFilters }) => {
  const handleGameChange = (gameId, checked) => {
    const newGames = checked
      ? [...(filters.games || []), gameId]
      : (filters.games || []).filter(g => g !== gameId);
    onFilterChange({ ...filters, games: newGames });
  };

  const handleCategoryChange = (categoryId, checked) => {
    const newCategories = checked
      ? [...(filters.categories || []), categoryId]
      : (filters.categories || []).filter(c => c !== categoryId);
    onFilterChange({ ...filters, categories: newCategories });
  };

  const handleRarityChange = (rarityId, checked) => {
    const newRarities = checked
      ? [...(filters.rarities || []), rarityId]
      : (filters.rarities || []).filter(r => r !== rarityId);
    onFilterChange({ ...filters, rarities: newRarities });
  };

  const handlePriceChange = (value) => {
    onFilterChange({ ...filters, minPrice: value[0], maxPrice: value[1] });
  };

  const hasActiveFilters = (filters.games?.length > 0) || 
                          (filters.categories?.length > 0) || 
                          (filters.rarities?.length > 0) ||
                          (filters.promoOnly) ||
                          (filters.minPrice > 0) ||
                          (filters.maxPrice < 200);

  // Build available rarities grouped by game
  const selectedGames = filters.games || [];
  const gamesToShow = selectedGames.length > 0
    ? selectedGames.filter(g => RARITIES_BY_GAME[g])
    : Object.keys(RARITIES_BY_GAME);

  const GAME_LABELS = { yugioh: 'Yu-Gi-Oh!', magic: 'Magic', pokemon: 'Pokémon' };

  return (
    <aside className="w-full lg:w-64 bg-[#0a0a0a] rounded-xl p-6 border border-white/10 h-fit sticky top-20" data-testid="filter-sidebar">
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-white font-semibold font-outfit text-lg">Filtres</h3>
        {hasActiveFilters && (
          <Button
            variant="ghost"
            size="sm"
            onClick={onClearFilters}
            className="text-gray-400 hover:text-white"
            data-testid="clear-filters-btn"
          >
            <X className="w-4 h-4 mr-1" />
            Effacer
          </Button>
        )}
      </div>

      {/* Promo Filter */}
      <div className="mb-6">
        <div className="flex items-center space-x-2">
          <Checkbox
            id="filter-promo"
            checked={filters.promoOnly || false}
            onCheckedChange={(checked) => onFilterChange({ ...filters, promoOnly: checked })}
            className="border-red-500/50 data-[state=checked]:bg-red-600 data-[state=checked]:border-red-600"
            data-testid="filter-promo"
          />
          <Label
            htmlFor="filter-promo"
            className="text-red-400 text-sm cursor-pointer hover:text-red-300 transition-colors font-medium"
          >
            En promo
          </Label>
        </div>
        <div className="flex items-center space-x-2 mt-2">
          <Checkbox
            id="filter-preorder"
            checked={filters.preorderOnly || false}
            onCheckedChange={(checked) => onFilterChange({ ...filters, preorderOnly: checked })}
            className="border-cyan-500/50 data-[state=checked]:bg-cyan-600 data-[state=checked]:border-cyan-600"
            data-testid="filter-preorder"
          />
          <Label
            htmlFor="filter-preorder"
            className="text-cyan-400 text-sm cursor-pointer hover:text-cyan-300 transition-colors font-medium"
          >
            En précommande
          </Label>
        </div>
      </div>

      {/* Games Filter */}
      <div className="mb-6">
        <h4 className="text-gray-400 text-sm font-medium mb-3 uppercase tracking-wider">Jeu</h4>
        <div className="space-y-3">
          {games.map((game) => (
            <div key={game.id} className="flex items-center space-x-3">
              <Checkbox
                id={`game-${game.id}`}
                checked={(filters.games || []).includes(game.id)}
                onCheckedChange={(checked) => handleGameChange(game.id, checked)}
                className="border-white/30 data-[state=checked]:bg-fuchsia-600 data-[state=checked]:border-fuchsia-600"
                data-testid={`filter-game-${game.id}`}
              />
              <Label
                htmlFor={`game-${game.id}`}
                className="text-gray-300 text-sm cursor-pointer hover:text-white transition-colors"
              >
                {game.label}
              </Label>
            </div>
          ))}
        </div>
      </div>

      {/* Category Filter */}
      <div className="mb-6">
        <h4 className="text-gray-400 text-sm font-medium mb-3 uppercase tracking-wider">Catégorie</h4>
        <div className="space-y-3">
          {categories.map((category) => (
            <div key={category.id} className="flex items-center space-x-3">
              <Checkbox
                id={`category-${category.id}`}
                checked={(filters.categories || []).includes(category.id)}
                onCheckedChange={(checked) => handleCategoryChange(category.id, checked)}
                className="border-white/30 data-[state=checked]:bg-fuchsia-600 data-[state=checked]:border-fuchsia-600"
                data-testid={`filter-category-${category.id}`}
              />
              <Label
                htmlFor={`category-${category.id}`}
                className="text-gray-300 text-sm cursor-pointer hover:text-white transition-colors"
              >
                {category.label}
              </Label>
            </div>
          ))}
        </div>
      </div>

      {/* Rarity Filter */}
      {gamesToShow.length > 0 && (
        <div>
          <h4 className="text-gray-400 text-sm font-medium mb-3 uppercase tracking-wider">Rareté</h4>
          <div className="space-y-4 max-h-60 overflow-auto pr-1">
            {gamesToShow.map(gameKey => (
              <div key={gameKey}>
                {gamesToShow.length > 1 && (
                  <p className="text-gray-500 text-xs font-semibold uppercase tracking-wider mb-2">{GAME_LABELS[gameKey] || gameKey}</p>
                )}
                <div className="space-y-2">
                  {(RARITIES_BY_GAME[gameKey] || []).map(rarity => (
                    <div key={`${gameKey}-${rarity.id}`} className="flex items-center space-x-2">
                      <Checkbox
                        id={`rarity-${gameKey}-${rarity.id}`}
                        checked={(filters.rarities || []).includes(rarity.id)}
                        onCheckedChange={(checked) => handleRarityChange(rarity.id, checked)}
                        className="border-white/30 data-[state=checked]:bg-fuchsia-600 data-[state=checked]:border-fuchsia-600"
                        data-testid={`filter-rarity-${rarity.id}`}
                      />
                      <Label
                        htmlFor={`rarity-${gameKey}-${rarity.id}`}
                        className="text-gray-300 text-sm cursor-pointer hover:text-white transition-colors"
                      >
                        {rarity.label}
                      </Label>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Price Filter */}
      <div>
        <h4 className="text-gray-400 text-sm font-medium mb-3 uppercase tracking-wider">
          Prix: {filters.minPrice ?? 0}€ – {filters.maxPrice >= 200 ? '200+' : `${filters.maxPrice}€`}
        </h4>
        <Slider
          value={[filters.minPrice ?? 0, filters.maxPrice ?? 200]}
          max={200}
          min={0}
          step={5}
          onValueChange={handlePriceChange}
          className="[&_[role=slider]]:bg-fuchsia-600"
          data-testid="price-slider"
        />
        <div className="flex justify-between text-xs text-gray-500 mt-2">
          <span>0€</span>
          <span>200+€</span>
        </div>
      </div>
    </aside>
  );
};
